import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

function DashboardPage() {
  const navigate = useNavigate();

  const [stats, setStats] = useState([
    {
      title: "Total Vendors",
      value: "0",
      sub: "Loading...",
      icon: "◫",
    },
    {
      title: "Active Sites",
      value: "0",
      sub: "Loading...",
      icon: "⌂",
    },
    {
      title: "Purchase Orders",
      value: "0",
      sub: "Loading...",
      icon: "🛒",
    },
    {
      title: "Expenses",
      value: "₹ 0",
      sub: "Current month",
      icon: "₹",
    },
  ]);

  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/dashboard-stats`);
      const dashboard = await response.json();
      if (!response.ok) throw new Error(dashboard.error || "Unable to load dashboard data");

      setStats([
        {
          title: "Total Vendors",
          value: dashboard.vendor_count || 0,
          sub: "Registered vendors",
          icon: "◫",
        },
        {
          title: "Active Sites",
          value: dashboard.site_count || 0,
          sub: "Project sites",
          icon: "⌂",
        },
        {
          title: "Purchase Orders",
          value: dashboard.po_count || 0,
          sub: "Total POs",
          icon: "🛒",
        },
        {
          title: "Expenses",
          value: `₹ ${Number(dashboard.po_total || 0).toLocaleString("en-IN")}`,
          sub: "PO value",
          icon: "₹",
        },
      ]);

      setRecentOrders(Array.isArray(dashboard.recent_orders) ? dashboard.recent_orders : []);
    } catch (err) {
      console.error("Unable to load dashboard:", err);
    }
  };

  return (
    <div 
  style={{ 
    padding: "40px", 
    background: "#f8fafc",
    minHeight: "100%", 
    boxSizing: "border-box" 
  }}
>
      {/* PAGE TITLE - LOCKED LIGHT THEME */}
      <div style={{ marginBottom: "25px" }}>
        <div
          style={{
            fontSize: "34px",
            fontWeight: "700",
            color: "#111827", // Permanent deep slate/black text
            letterSpacing: "-0.02em",
            marginBottom: "6px",
          }}
        >
          Dashboard
        </div>
        <div style={{ fontSize: "13px", color: "#6b7280" }}>
          Welcome back. Here's your business overview.
        </div>
      </div>

      {/* STATS MATRIX - LOCKED PURE WHITE CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "18px",
          marginBottom: "25px",
        }}
      >
        {stats.map((item, index) => (
          <div
            key={index}
            style={{
              background: "#ffffff", // Locked White
              border: "1px solid #e5e7eb", // Locked light border
              borderRadius: "14px",
              padding: "22px",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>
                {item.title}
              </div>
              <div
                style={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "10px",
                  background: "#f3f4f6", // Locked light icon background
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "15px",
                  color: "#111827"
                }}
              >
                {item.icon}
              </div>
            </div>

            <div>
              <div style={{ fontSize: "30px", fontWeight: "700", color: "#111827", marginBottom: "5px", letterSpacing: "-0.01em" }}>
                {item.value}
              </div>
              <div style={{ fontSize: "12px", color: "#10b981", fontWeight: "500" }}>
                {item.sub}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MAIN TWO COLUMN GRID */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "20px" }}>
        
        {/* RECENT ORDERS PANEL - LOCKED WHITE */}
        <div
          style={{
            background: "#ffffff", // Locked White
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            padding: "22px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>
              Recent Orders
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "3px" }}>
              Latest purchase activities
            </div>
          </div>

          <div>
            {recentOrders.length === 0 ? (
              <div style={{ padding: "30px 0", textAlign: "center", color: "#6b7280", fontSize: "13px" }}>
                No recent purchase orders logged.
              </div>
            ) : (
              recentOrders.map((order, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 0",
                    borderBottom: index !== recentOrders.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                      {order.order_id || `PO #${order.id}`}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                      {order.vendor_name}
                    </div>
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#111827" }}>
                    ₹ {Number(order.total_amount || 0).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* QUICK ACTIONS PANEL - LOCKED WHITE */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div
            style={{
              background: "#ffffff", // Locked White
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "22px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            }}
          >
            <div style={{ fontSize: "18px", fontWeight: "600", marginBottom: "18px", color: "#111827" }}>
              Quick Actions
            </div>

            <div style={{ display: "grid", gap: "12px" }}>
              <button 
                onClick={() => navigate("/rfq")} 
                style={actionBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#ffffff"}
              >
                + Create RFQ
              </button>

              <button 
                onClick={() => navigate("/vendors")} 
                style={actionBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#ffffff"}
              >
                + Add Vendor
              </button>

              <button 
                onClick={() => navigate("/sites")} 
                style={actionBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#ffffff"}
              >
                + Add Site
              </button>

              <button 
                onClick={() => navigate("/purchase-order")} 
                style={actionBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#ffffff"}
              >
                + New PO
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// Clean Static Style Object with locked light scheme colors
const actionBtnStyle = {
  border: "1px solid #e5e7eb",
  background: "#ffffff",
  padding: "13px",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: "600",
  color: "#111827",
  textAlign: "left",
  transition: "all 0.15s ease"
};

export default DashboardPage;
